<div class="space-y-6">

  <!-- Loading -->
  <div *ngIf="loading" class="flex items-center justify-center py-16">
    <lucide-icon name="loader" [size]="22" class="animate-spin text-violet-500" [strokeWidth]="2"></lucide-icon>
  </div>

  <!-- Error -->
  <div *ngIf="!loading && error" class="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
    <lucide-icon name="triangle-alert" [size]="15" class="mt-0.5 flex-shrink-0 text-red-500" [strokeWidth]="2"></lucide-icon>
    <p class="text-sm text-red-700">{{ error }}</p>
  </div>

  <!-- No org yet -->
  <div *ngIf="!loading && !error && !org"
       class="rounded-xl border border-slate-200 bg-white px-6 py-12 text-center shadow-sm">
    <lucide-icon name="users" [size]="32" class="mx-auto mb-3 text-slate-300" [strokeWidth]="1.5"></lucide-icon>
    <p class="text-sm font-medium text-slate-700">No organization yet</p>
    <p class="mt-1 text-xs text-slate-400">Your organization is created automatically when you subscribe to a plan.</p>
  </div>

  <ng-container *ngIf="!loading && !error && org">

    <!-- Org info -->
    <div class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div class="border-b border-slate-200 px-6 py-4">
        <h2 class="text-sm font-semibold text-slate-900">Organization</h2>
      </div>
      <div class="divide-y divide-slate-100">

        <!-- Name row -->
        <div class="px-6 py-4">
          <p class="mb-1.5 text-xs font-medium uppercase tracking-wide text-slate-400">Name</p>

          <!-- Display mode -->
          <div *ngIf="!editingName" class="flex items-center gap-2">
            <p class="text-sm font-medium text-slate-900">{{ org.name }}</p>
            <button
              *ngIf="isOwner"
              (click)="startEditingName()"
              title="Edit name"
              class="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
            >
              <lucide-icon name="pencil" [size]="13" [strokeWidth]="2"></lucide-icon>
            </button>
          </div>

          <!-- Edit mode -->
          <div *ngIf="editingName" class="space-y-2">
            <div class="flex items-center gap-2">
              <input
                type="text"
                [(ngModel)]="nameInput"
                (keydown.enter)="saveName()"
                (keydown.escape)="cancelEditingName()"
                maxlength="100"
                autofocus
                class="block w-64 rounded-lg border border-violet-400 bg-white px-3 py-1.5 text-sm text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-100"
              />
              <button
                (click)="saveName()"
                [disabled]="nameSaving || !nameInput.trim()"
                class="flex items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <lucide-icon *ngIf="nameSaving" name="loader" [size]="12" class="animate-spin" [strokeWidth]="2.5"></lucide-icon>
                {{ nameSaving ? 'Saving…' : 'Save' }}
              </button>
              <button
                (click)="cancelEditingName()"
                [disabled]="nameSaving"
                class="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-500 transition hover:bg-slate-100 disabled:opacity-60"
              >
                Cancel
              </button>
            </div>
            <p *ngIf="nameError" class="text-xs text-red-600">{{ nameError }}</p>
          </div>
        </div>

        <div class="px-6 py-4">
          <p class="mb-0.5 text-xs font-medium uppercase tracking-wide text-slate-400">Your role</p>
          <span class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                [class.bg-violet-100]="myMembership?.role === 'owner'"
                [class.text-violet-700]="myMembership?.role === 'owner'"
                [class.bg-blue-100]="myMembership?.role === 'admin'"
                [class.text-blue-700]="myMembership?.role === 'admin'"
                [class.bg-slate-100]="myMembership?.role === 'member'"
                [class.text-slate-600]="myMembership?.role === 'member'">
            {{ myMembership?.role | titlecase }}
          </span>
        </div>
      </div>
    </div>

    <!-- Members -->
    <div class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div class="flex items-center justify-between border-b border-slate-200 px-6 py-4">
        <div>
          <h2 class="text-sm font-semibold text-slate-900">Members</h2>
          <p class="mt-0.5 text-xs text-slate-500">{{ members.length }} active member{{ members.length === 1 ? '' : 's' }}</p>
        </div>
        <span class="rounded-full px-2.5 py-1 text-xs font-semibold"
              [class.bg-amber-100]="seatUsed >= seatLimit"
              [class.text-amber-700]="seatUsed >= seatLimit"
              [class.bg-slate-100]="seatUsed < seatLimit"
              [class.text-slate-600]="seatUsed < seatLimit">
          {{ seatUsed }} / {{ seatLimit }} seats
        </span>
      </div>

      <ul class="divide-y divide-slate-100">
        <li *ngFor="let member of members; trackBy: trackByUserId"
            class="flex items-center justify-between gap-4 px-6 py-4">
          <div class="min-w-0">
            <p class="truncate text-sm font-medium text-slate-900">
              {{ member.givenName }} {{ member.familyName }}
              <span *ngIf="member.userId === currentUserId" class="ml-1.5 text-xs text-slate-400">(you)</span>
            </p>
            <p class="truncate text-xs text-slate-400">{{ member.email }}</p>
          </div>

          <div class="flex flex-shrink-0 items-center gap-3">
            <!-- Role selector — admin/owner only, cannot change own role if only owner -->
            <select
              *ngIf="isAdminOrOwner"
              [ngModel]="member.role"
              (change)="changeRole(member.userId, $any($event.target).value)"
              [disabled]="updatingMemberId === member.userId || (!isOwner && member.role === 'admin') || (!isOwner && member.role === 'owner')"
              class="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 transition focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
              <option *ngIf="isOwner" value="owner">Owner</option>
            </select>

            <span *ngIf="!isAdminOrOwner"
                  class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                  [class.bg-violet-100]="member.role === 'owner'"
                  [class.text-violet-700]="member.role === 'owner'"
                  [class.bg-blue-100]="member.role === 'admin'"
                  [class.text-blue-700]="member.role === 'admin'"
                  [class.bg-slate-100]="member.role === 'member'"
                  [class.text-slate-600]="member.role === 'member'">
              {{ member.role | titlecase }}
            </span>

            <!-- Remove button — cannot remove yourself unless you're not the last owner -->
            <button
              *ngIf="isAdminOrOwner && member.userId !== currentUserId"
              (click)="removeMember(member.userId)"
              title="Remove member"
              class="flex items-center rounded-lg p-1.5 text-slate-400 transition hover:bg-red-50 hover:text-red-500"
            >
              <lucide-icon name="x" [size]="14" [strokeWidth]="2.5"></lucide-icon>
            </button>
          </div>
        </li>
      </ul>
    </div>

    <!-- Invite form — admin/owner only -->
    <div *ngIf="isAdminOrOwner" class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div class="border-b border-slate-200 px-6 py-4">
        <h2 class="text-sm font-semibold text-slate-900">Invite a team member</h2>
        <p class="mt-0.5 text-xs text-slate-500">They'll receive an email with a 7-day invitation link.</p>
      </div>
      <div class="px-6 py-5">

        <!-- Seat limit reached -->
        <div *ngIf="!canInvite" class="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <lucide-icon name="users" [size]="15" class="mt-0.5 flex-shrink-0 text-amber-500" [strokeWidth]="2"></lucide-icon>
          <div>
            <p class="text-sm font-medium text-amber-800">Seat limit reached</p>
            <p class="mt-0.5 text-xs text-amber-700">Your {{ org?.subscriptionTier | titlecase }} plan includes {{ seatLimit }} seat{{ seatLimit === 1 ? '' : 's' }}. Upgrade your plan to invite more members.</p>
          </div>
        </div>

        <ng-container *ngIf="canInvite">
          <div *ngIf="inviteError" class="mb-4 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
            <lucide-icon name="triangle-alert" [size]="15" class="mt-0.5 flex-shrink-0 text-red-500" [strokeWidth]="2"></lucide-icon>
            <p class="text-sm text-red-700">{{ inviteError }}</p>
          </div>

          <div class="flex items-end gap-3">
            <div class="flex-1">
              <label for="invite-email" class="mb-1.5 block text-xs font-medium text-slate-700">Email address</label>
              <input
                id="invite-email"
                type="email"
                [(ngModel)]="inviteEmail"
                placeholder="colleague@example.com"
                class="block w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 transition-shadow focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100"
              />
            </div>
            <div class="w-32">
              <label for="invite-role" class="mb-1.5 block text-xs font-medium text-slate-700">Role</label>
              <select
                id="invite-role"
                [(ngModel)]="inviteRole"
                class="block w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-700 transition focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100"
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
                <option *ngIf="isOwner" value="owner">Owner</option>
              </select>
            </div>
            <button
              (click)="sendInvite()"
              [disabled]="inviteLoading || !inviteEmail.trim()"
              class="flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <lucide-icon *ngIf="inviteLoading" name="loader" [size]="15" class="animate-spin" [strokeWidth]="2.5"></lucide-icon>
              {{ inviteLoading ? 'Sending…' : 'Send invite' }}
            </button>
          </div>
        </ng-container>
      </div>
    </div>

    <!-- Pending invitations — admin/owner only -->
    <div *ngIf="isAdminOrOwner && invitations.length > 0"
         class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div class="border-b border-slate-200 px-6 py-4">
        <h2 class="text-sm font-semibold text-slate-900">Pending invitations</h2>
      </div>
      <ul class="divide-y divide-slate-100">
        <li *ngFor="let invite of invitations; trackBy: trackByInviteId"
            class="flex items-center justify-between gap-4 px-6 py-4">
          <div class="min-w-0">
            <p class="truncate text-sm font-medium text-slate-900">{{ invite.invitedEmail }}</p>
            <p class="text-xs text-slate-400">
              {{ invite.role | titlecase }} · Expires {{ invite.expiresAt | date:'mediumDate' }}
            </p>
          </div>
          <button
            (click)="revokeInvitation(invite.inviteId)"
            title="Revoke invitation"
            class="flex-shrink-0 rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-500 transition hover:bg-red-50 hover:text-red-500"
          >
            Revoke
          </button>
        </li>
      </ul>
    </div>

  </ng-container>

</div>
